import React, { ChangeEvent, useEffect, useState } from 'react';

import 'src/pages/Page.css';
import 'src/pages/Instellingen/BouwdeelPage.css';

import { Bouwdeel, Gateway, Verdieping } from 'src/common/types';
import { RES_TYPES, useFetch } from 'src/hooks/useFetch';
import { FaPlus, FaSave, FaTrash } from 'react-icons/fa';
import { DeleteComponent } from 'src/components/DeleteComponent';
import { VerdiepingenComponent } from './components/VerdiepingenComponent';

type Props = {
  bouwdeel?: Bouwdeel;
  oprdachtgever_id: string;
  refresh: () => void;
};

const createEmptyBouwdeel = (oprdachtgever_id: string) => {
  return {
    name: '',
    gateways: [] as Gateway[],
    description: '',
    doi_code: '',
    opdrachtgever_id: oprdachtgever_id,
  } as Bouwdeel;
};

export const BouwdeelPage = ({ bouwdeel, oprdachtgever_id, refresh }: Props) => {
  const [saveable, setSaveable] = useState<boolean>(false);
  const { res: resPost, fetchData: postBouwdeel } = useFetch('/api/bouwdeel', {
    method: 'POST',
    lateFetch: true,
    resType: RES_TYPES.empty,
    defaultValue: undefined,
  });
  const {
    res: resPut,
    fetchData: putBouwdeel,
    setUrl: setPutUrl,
  } = useFetch('', {
    method: 'PUT',
    lateFetch: true,
    resType: RES_TYPES.empty,
    defaultValue: undefined,
  });
  const { data: verdiepingData } = useFetch<Verdieping[]>('/api/verdieping', { defaultValue: [] });

  const [newBouwdeel, setNewBouwdeel] = useState<Bouwdeel>(createEmptyBouwdeel(oprdachtgever_id));
  const [newGateway, setNewGateway] = useState<Gateway>({
    ip_address: '',
    verdiepingen: [] as Verdieping[],
    vlan: '',
  });

  useEffect(() => {
    if (bouwdeel) {
      setPutUrl(`/api/bouwdeel/${bouwdeel.id}`);
      setNewBouwdeel(prevState => ({
        ...prevState,
        ...bouwdeel,
      }));
    }
  }, [bouwdeel]);

  useEffect(() => {
    if ((resPost && resPost.ok) || (resPut && resPut.ok)) {
      close();
    }
  }, [resPost, resPut]);

  useEffect(() => {
    setSaveable(newBouwdeel.name !== '' && isValidDOICode(newBouwdeel.doi_code) && isValidNetworkNumber(newBouwdeel.network_number));
  }, [newBouwdeel]);

  const close = () => {
    setNewBouwdeel(createEmptyBouwdeel(oprdachtgever_id));
    setNewGateway({ ip_address: '', verdiepingen: [], vlan: '' });
    setPutUrl('');
    refresh();
  };

  const updateBouwdeel = (event: ChangeEvent<HTMLInputElement>) => {
    setNewBouwdeel(prevState => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };

  const updateNetworkNumber = (event: ChangeEvent<HTMLInputElement>) => {
    setNewBouwdeel(prevState => ({
      ...prevState,
      network_number: event.target.value === '' ? null : +event.target.value,
    }));
  };

  const updateGateway = (key: string, value: string | Verdieping[]) => {
    setNewGateway(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  const addGateway = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    const parts = newGateway.ip_address.split('.');
    if (
      !(
        parts.length === 4 &&
        parts.every(part => parts.length > 0 && !isNaN(+part) && part != '' && Number(part) >= 0 && Number(part) < 255)
      )
    ) {
      alert('Gateway is niet correct');
      return;
    }
    if (newBouwdeel.gateways.some(gateway => gateway.ip_address === newGateway.ip_address)) {
      alert('Gateway bestaat al');
      return;
    }

    setNewBouwdeel(prevState => ({
      ...prevState,
      gateways: [...prevState.gateways, newGateway],
    }));
    setNewGateway({ ip_address: '', verdiepingen: [], vlan: '' });
  };

  const removeGateway = (index: number) => {
    setNewBouwdeel(prevState => ({
      ...prevState,
      gateways: prevState.gateways.filter((_, i) => i !== index),
    }));
  };

  const updateGatewayByIndex = (key: keyof Gateway, verdiepingen: Verdieping[] | string, index: number) => {
    setNewBouwdeel(prevState => ({
      ...prevState,
      gateways: prevState.gateways.map((gateway, i) => (i === index ? { ...gateway, [key]: verdiepingen } : gateway)),
    }));
  };

  const addBouwdeel = () => {
    if (bouwdeel) {
      putBouwdeel(JSON.stringify(newBouwdeel));
    } else {
      postBouwdeel(JSON.stringify(newBouwdeel));
    }
  };

  const isValidNetworkNumber = (network_number: number | undefined | null) => {
    return network_number == undefined || (network_number >= 0 && network_number < 65535);
  };

  const isValidDOICode = (doi_code: string) => {
    return doi_code != null && doi_code.length === 1;
  };

  return (
    <div className="chair-item">
      <h2>{bouwdeel ? bouwdeel.name : 'Nieuw'}</h2>
      <div className="chair-item-header">
        <FaSave
          className={'update-icon ' + (!saveable ? ' disabled' : '')}
          onClick={() => addBouwdeel()}
          title="Opslaan"
          data-testid="save-button"
        />
        {bouwdeel && <DeleteComponent path={`/api/bouwdeel/${bouwdeel.id}`} name={newBouwdeel.name} refresh={close} />}
      </div>
      <div className="property">
        <label htmlFor="name">Naam</label>
        <input
          maxLength={4}
          type="text"
          id="name"
          name="name"
          value={newBouwdeel.name}
          onChange={updateBouwdeel}
          placeholder="Bouwdeelcode 4 karakters"></input>
      </div>
      <div className="property">
        <label htmlFor="description">Beschrijving</label>
        <input
          maxLength={255}
          type="text"
          id="description"
          name="description"
          value={newBouwdeel.description}
          onChange={updateBouwdeel}></input>
      </div>
      <div className="property">
        <label htmlFor="doi_code">DOI code</label>
        <input
          max={1}
          type="number"
          id="doi_code"
          name="doi_code"
          value={newBouwdeel.doi_code}
          className={!isValidDOICode(newBouwdeel.doi_code) ? 'error' : ''}
          onChange={updateBouwdeel}
          placeholder="DOI code 1 getal"></input>
      </div>
      <div className="property">
        <label htmlFor="doi_code">Netwerknummer</label>
        <input
          type="number"
          id="network_number"
          name="network_number"
          value={newBouwdeel.network_number || ''}
          className={!isValidNetworkNumber(newBouwdeel.network_number) ? 'error' : ''}
          onChange={updateNetworkNumber}
          placeholder="Default Netwerknummer"></input>
      </div>
      <div className="property">
        <label htmlFor="gateway">Gateways</label>
        <table>
          <thead>
            <tr>
              <th>IP Address</th>
              <th>Verdiepingen</th>
              <th>VLAN</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {newBouwdeel.gateways.map((gateway, index) => (
              <tr key={index} className="gateway-item">
                <td>{gateway.ip_address}</td>
                <td>
                  <VerdiepingenComponent
                    verdiepingen={verdiepingData}
                    selectedVerdiepingen={gateway.verdiepingen}
                    updateVerdiepingen={updateGatewayByIndex}
                    index={index}></VerdiepingenComponent>
                </td>
                <td>
                  <input
                    data-testid="vlan"
                    type="text"
                    value={gateway.vlan || ''}
                    onChange={event => updateGatewayByIndex('vlan', event.target.value, index)}
                    placeholder="VLAN"
                  />
                </td>
                <td>
                  <FaTrash className="gateway-remove" onClick={() => removeGateway(index)} title="Verwijder Gateway" />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <input
                  type="text"
                  id="gateway"
                  name="gateway"
                  data-testid="new-gateway"
                  className="gateway-input"
                  value={newGateway.ip_address}
                  placeholder="Nieuwe Gateway"
                  onChange={event => updateGateway('ip_address', event.target.value)}
                  onKeyDown={addGateway}></input>
              </td>
              <td>
                <VerdiepingenComponent
                  verdiepingen={verdiepingData}
                  selectedVerdiepingen={newGateway.verdiepingen}
                  updateVerdiepingen={updateGateway}></VerdiepingenComponent>
              </td>
              <td>
                <input
                  type="text"
                  id="vlan"
                  name="vlan"
                  data-testid="new-vlan"
                  className="vlan-input"
                  value={newGateway.vlan}
                  placeholder="VLAN"
                  onChange={event => updateGateway('vlan', event.target.value)}></input>
              </td>
              <td>
                <FaPlus
                  className="update-icon"
                  onClick={() => addGateway({ key: 'Enter' } as React.KeyboardEvent<HTMLInputElement>)}
                  title="Add"
                />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {bouwdeel && (
        <div className="chair-item-footer">
          <button onClick={() => close()}>Cancel</button>
        </div>
      )}
    </div>
  );
};
